import React, { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import FitnessBackgroundCanvas from "../components/FitnessBackgroundCanvas";

const FITNESS_METRICS = [
  { id: "CARDIO", name: "❤️ CARDIO", stat: "142 BPM", zone: "Peak Zone 4", isUp: true },
  { id: "BURN", name: "🔥 CALORIE BURN", stat: "650 KCAL", zone: "Active Goal", isUp: true },
  { id: "PR", name: "⚡ STRENGTH PR", stat: "95 KG", zone: "Bench Record", isUp: true },
  { id: "STREAK", name: "🏆 WARRIOR STREAK", stat: "18 DAYS", zone: "Unbroken", isUp: true },
  { id: "MACROS", name: "🥗 PROTEIN FUEL", stat: "140g", zone: "Target Met", isUp: false },
  { id: "HYDRATION", name: "💧 HYDRATION", stat: "2.8L", zone: "Hydrated", isUp: true },
  { id: "RECOVERY", name: "🔋 RECOVERY SCORE", stat: "94%", zone: "Optimal", isUp: true },
];

function Login({ darkMode, setDarkMode, setToken }) {
  const [authMode, setAuthMode] = useState("login"); // 'login' | 'signup'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("amandeepverma3001@gmail.com");
  const [password, setPassword] = useState("warrior2026");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);

  // Live Metrics Ticker State
  const [metrics, setMetrics] = useState(FITNESS_METRICS);
  const [activeMetric, setActiveMetric] = useState(FITNESS_METRICS[0]);

  const navigate = useNavigate();
  const isDark = darkMode !== false;

  // Gentle live fluctuation for the top fitness ribbon
  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics((prev) =>
        prev.map((m) => {
          if (m.id === "CARDIO") {
            const bpm = 138 + Math.floor(Math.random() * 8);
            return { ...m, stat: `${bpm} BPM` };
          }
          if (m.id === "BURN") {
            const kcal = 645 + Math.floor(Math.random() * 12);
            return { ...m, stat: `${kcal} KCAL` };
          }
          return m;
        })
      );
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  const toggleTheme = () => {
    if (setDarkMode) {
      setDarkMode(!isDark);
    }
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (loading || authSuccess) return;
    setError("");

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your email address!");
      return;
    }

    if (!password) {
      setError("Please enter your password!");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email: trimmedEmail, password });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setAuthSuccess(true);
      setTimeout(() => {
        if (setToken) setToken(res.data.token);
        navigate("/dashboard");
      }, 400);
    } catch (err) {
      // If server error or credentials mismatch, provide clear feedback or allow demo
      const errMsg = err.response?.data?.error || "Login failed. Check server or use One-Click Demo Access below!";
      setError(errMsg);
    }
    setLoading(false);
  };

  const handleSignup = async (e) => {
    if (e) e.preventDefault();
    if (loading || authSuccess) return;
    setError("");

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) {
      setError("Please enter your full name!");
      return;
    }
    if (!trimmedEmail) {
      setError("Please enter your email address!");
      return;
    }
    if (!password || password.length < 4) {
      setError("Password must be at least 4 characters long!");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/auth/signup", {
        name: trimmedName,
        email: trimmedEmail,
        password,
      });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setAuthSuccess(true);
      setTimeout(() => {
        if (setToken) setToken(res.data.token);
        navigate("/dashboard");
      }, 400);
    } catch (err) {
      setError(err.response?.data?.error || "Signup failed. Please try again!");
    }
    setLoading(false);
  };

  // One-Click Demo Access: Instant access with loaded warrior stats
  const handleInstantDemoLogin = async () => {
    setEmail("amandeepverma3001@gmail.com");
    setPassword("warrior2026");
    setLoading(true);
    setError("");

    // Try backend authentication first
    try {
      const res = await api.post("/auth/login", {
        email: "amandeepverma3001@gmail.com",
        password: "warrior2026",
      });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setAuthSuccess(true);
      setTimeout(() => {
        if (setToken) setToken(res.data.token);
        navigate("/dashboard");
      }, 350);
      return;
    } catch (e) {
      // Fallback: seed full verified athlete session instantly
      const demoWarrior = {
        id: "demo_warrior_amandeep_001",
        name: "Amandeep Verma",
        email: "amandeepverma3001@gmail.com",
        streak: 18,
        weight: 74,
        age: 22,
        todayCalories: 820,
        workoutsCompleted: 42,
      };
      const demoToken = "demo_warrior_jwt_session_token_2026";
      localStorage.setItem("token", demoToken);
      localStorage.setItem("user", JSON.stringify(demoWarrior));
      setAuthSuccess(true);
      setTimeout(() => {
        if (setToken) setToken(demoToken);
        navigate("/dashboard");
      }, 350);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        overflowX: "hidden",
        color: isDark ? "#ffffff" : "#0f172a",
        transition: "background 0.3s ease, color 0.3s ease",
      }}
    >
      {/* 1. DYNAMIC MOVING FITNESS BACKGROUND CANVAS (ECG WAVE + EMBERS + SCANNER) */}
      <FitnessBackgroundCanvas activeMetric={activeMetric.id} isDarkMode={isDark} />

      {/* 2. TOP HEADER & INTERACTIVE LIVE FITNESS METRICS RIBBON */}
      <div style={{ position: "relative", zIndex: 10 }}>
        <header
          style={{
            padding: "14px 28px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            background: isDark ? "rgba(11, 7, 24, 0.85)" : "rgba(255, 255, 255, 0.88)",
            borderBottom: isDark ? "1px solid rgba(255, 107, 53, 0.15)" : "1px solid rgba(0, 0, 0, 0.08)",
            transition: "all 0.3s ease",
          }}
        >
          {/* BRAND LOGO */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #ff6b35 0%, #D85A30 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px rgba(255, 107, 53, 0.45)",
                fontSize: "1.2rem",
                color: "#ffffff",
              }}
            >
              ⚡
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                style={{
                  fontSize: "1.45rem",
                  fontWeight: "800",
                  letterSpacing: "-0.8px",
                  background: "linear-gradient(135deg, #ff6b35, #ffb347)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Ignite
              </span>
              <span
                style={{
                  fontSize: "0.68rem",
                  fontWeight: "800",
                  letterSpacing: "0.5px",
                  background: isDark ? "rgba(255, 107, 53, 0.15)" : "rgba(255, 107, 53, 0.1)",
                  color: "#ff6b35",
                  border: "1px solid rgba(255, 107, 53, 0.3)",
                  padding: "3px 8px",
                  borderRadius: "50px",
                }}
              >
                ATHLETE OS v2.0
              </span>
            </div>
          </div>

          {/* STATUS PILL, THEME TOGGLE & QUICK ENTER */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 14px",
                borderRadius: "50px",
                background: isDark ? "rgba(16, 185, 129, 0.15)" : "#f0fdf4",
                border: isDark ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid #bbf7d0",
                fontSize: "0.78rem",
                fontWeight: "700",
                color: isDark ? "#34d399" : "#16a34a",
              }}
            >
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: "#10b981",
                  boxShadow: "0 0 8px #10b981",
                }}
              />
              <span>Biometrics Live (18ms) • AI Coach Ready</span>
            </div>

            {/* LIGHT / DARK THEME TOGGLE BUTTON */}
            <button
              onClick={toggleTheme}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "7px 14px",
                borderRadius: "50px",
                cursor: "pointer",
                border: isDark ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #cbd5e1",
                background: isDark ? "rgba(255, 255, 255, 0.08)" : "#ffffff",
                color: isDark ? "#f59e0b" : "#475569",
                fontSize: "0.82rem",
                fontWeight: "700",
                transition: "all 0.2s ease",
                boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.06)",
              }}
              title={isDark ? "Switch to Light Theme" : "Switch to Dark Theme"}
            >
              {isDark ? (
                <>
                  <span style={{ fontSize: "1rem" }}>☀️</span>
                  <span style={{ color: "#f1f5f9" }}>Light</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: "1rem" }}>🌙</span>
                  <span style={{ color: "#0f172a" }}>Dark</span>
                </>
              )}
            </button>

            {/* QUICK ENTER BUTTON */}
            <button
              onClick={handleInstantDemoLogin}
              style={{
                padding: "8px 18px",
                fontSize: "0.85rem",
                cursor: "pointer",
                border: "none",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #ff6b35 0%, #D85A30 100%)",
                color: "#ffffff",
                fontWeight: "700",
                boxShadow: "0 4px 14px rgba(255, 107, 53, 0.35)",
                transition: "all 0.2s ease",
              }}
            >
              Enter Arena ⚡
            </button>
          </div>
        </header>

        {/* CLICKABLE LIVE FITNESS METRICS TICKER RIBBON */}
        <div
          style={{
            background: isDark ? "rgba(13, 8, 28, 0.92)" : "rgba(245, 247, 255, 0.92)",
            borderBottom: isDark ? "1px solid rgba(255, 107, 53, 0.12)" : "1px solid rgba(0, 0, 0, 0.06)",
            padding: "8px 24px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            overflowX: "auto",
            scrollbarWidth: "none",
            transition: "all 0.3s ease",
          }}
        >
          <span
            style={{
              fontSize: "0.74rem",
              fontWeight: "800",
              color: isDark ? "rgba(255, 255, 255, 0.5)" : "#64748b",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            LIVE TELEMETRY (CLICK TO FOCUS):
          </span>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {metrics.map((m) => {
              const isSelected = activeMetric.id === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setActiveMetric(m)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "5px 13px",
                    borderRadius: "50px",
                    background: isSelected
                      ? isDark
                        ? "rgba(255, 107, 53, 0.25)"
                        : "rgba(255, 107, 53, 0.15)"
                      : isDark
                      ? "rgba(255, 255, 255, 0.06)"
                      : "#ffffff",
                    border: isSelected
                      ? "1.5px solid #ff6b35"
                      : isDark
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.08)",
                    color: isSelected ? "#ff6b35" : isDark ? "#e2e8f0" : "#334155",
                    fontSize: "0.8rem",
                    fontWeight: "700",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    boxShadow: isSelected ? "0 2px 10px rgba(255, 107, 53, 0.3)" : "none",
                    transition: "all 0.15s ease",
                  }}
                >
                  <span>{m.name}</span>
                  <span style={{ color: isDark ? "#ffffff" : "#0f172a" }}>{m.stat}</span>
                  <span
                    style={{
                      color: m.isUp ? "#10b981" : "#06b6d4",
                      fontSize: "0.72rem",
                      fontWeight: "700",
                    }}
                  >
                    • {m.zone}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. CENTER PORTAL CARD (LOGIN / SIGNUP) */}
      <main
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "36px 20px",
          flex: 1,
        }}
      >
        <div
          className="fitness-card-container"
          style={{
            width: "100%",
            maxWidth: "440px",
            background: isDark ? "rgba(18, 11, 35, 0.88)" : "rgba(255, 255, 255, 0.92)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            borderRadius: "26px",
            boxShadow: isDark
              ? "0 30px 80px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(255, 107, 53, 0.2), 0 0 40px rgba(255, 107, 53, 0.12)"
              : "0 25px 60px -15px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 107, 53, 0.2)",
            padding: "38px 34px",
            position: "relative",
            overflow: "hidden",
            animation: "fadeInUp 0.35s ease-out",
            transition: "all 0.3s ease",
          }}
        >
          {/* TOP ORANGE ACCENT GLOW LINE */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ff6b35 0%, #ffb347 50%, #D85A30 100%)",
            }}
          />

          {/* CARD HEADER */}
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                width: "54px",
                height: "54px",
                margin: "0 auto 12px auto",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #ff6b35 0%, #D85A30 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.6rem",
                color: "#ffffff",
                boxShadow: "0 8px 24px rgba(255, 107, 53, 0.5)",
              }}
            >
              ⚡
            </div>

            <h1
              style={{
                fontSize: "1.85rem",
                fontWeight: "800",
                margin: "0 0 6px 0",
                letterSpacing: "-0.5px",
                background: "linear-gradient(135deg, #ff6b35, #ffb347)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {authMode === "signup" ? "Join the Vanguard" : "Enter the Arena"}
            </h1>
            <p
              style={{
                color: isDark ? "rgba(255, 255, 255, 0.6)" : "#64748b",
                fontSize: "0.88rem",
                margin: 0,
              }}
            >
              {authMode === "signup"
                ? "Start your autonomous fitness journey today 🔥"
                : "Welcome back, warrior! Level up your training 🔥"}
            </p>
          </div>

          {/* TAB SWITCHER */}
          <div
            style={{
              display: "flex",
              background: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.05)",
              borderRadius: "12px",
              padding: "4px",
              marginBottom: "20px",
            }}
          >
            <button
              type="button"
              onClick={() => {
                setAuthMode("login");
                setError("");
              }}
              style={{
                flex: 1,
                padding: "9px",
                borderRadius: "9px",
                border: "none",
                fontWeight: "700",
                fontSize: "0.86rem",
                cursor: "pointer",
                background:
                  authMode === "login"
                    ? isDark
                      ? "#24183d"
                      : "#ffffff"
                    : "transparent",
                color: authMode === "login" ? "#ff6b35" : isDark ? "rgba(255, 255, 255, 0.55)" : "#64748b",
                boxShadow: authMode === "login" ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                transition: "all 0.2s",
              }}
            >
              🚀 Login
            </button>
            <button
              type="button"
              onClick={() => {
                setAuthMode("signup");
                setError("");
              }}
              style={{
                flex: 1,
                padding: "9px",
                borderRadius: "9px",
                border: "none",
                fontWeight: "700",
                fontSize: "0.86rem",
                cursor: "pointer",
                background:
                  authMode === "signup"
                    ? isDark
                      ? "#24183d"
                      : "#ffffff"
                    : "transparent",
                color: authMode === "signup" ? "#ff6b35" : isDark ? "rgba(255, 255, 255, 0.55)" : "#64748b",
                boxShadow: authMode === "signup" ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                transition: "all 0.2s",
              }}
            >
              ✨ Sign Up
            </button>
          </div>

          {/* ONE-CLICK DEMO ACCESS BUTTON */}
          <button
            type="button"
            onClick={handleInstantDemoLogin}
            disabled={loading || authSuccess}
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "12px",
              background: isDark ? "rgba(255, 107, 53, 0.14)" : "rgba(255, 107, 53, 0.1)",
              border: "1.5px dashed #ff6b35",
              color: "#ff6b35",
              fontWeight: "800",
              fontSize: "0.9rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              cursor: "pointer",
              marginBottom: "20px",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.background = isDark
                ? "rgba(255, 107, 53, 0.24)"
                : "rgba(255, 107, 53, 0.18)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.background = isDark
                ? "rgba(255, 107, 53, 0.14)"
                : "rgba(255, 107, 53, 0.1)")
            }
          >
            <span>⚡ One-Click Athlete Demo (Amandeep)</span>
          </button>

          {/* ERROR ALERT */}
          {error && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.15)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                borderRadius: "10px",
                padding: "10px 14px",
                marginBottom: "16px",
                color: "#f87171",
                fontSize: "0.82rem",
                textAlign: "center",
                fontWeight: "600",
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {/* FORM */}
          <form onSubmit={authMode === "signup" ? handleSignup : handleLogin}>
            {authMode === "signup" && (
              <div style={{ marginBottom: "14px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.82rem",
                    fontWeight: "700",
                    color: isDark ? "rgba(255, 255, 255, 0.7)" : "#475569",
                    marginBottom: "5px",
                  }}
                >
                  👤 Full Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Amandeep Verma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: "10px",
                    border: isDark ? "1px solid rgba(255, 255, 255, 0.14)" : "1px solid #cbd5e1",
                    background: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
                    color: isDark ? "#ffffff" : "#0f172a",
                    fontSize: "0.92rem",
                    fontWeight: "500",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            )}

            {/* EMAIL */}
            <div style={{ marginBottom: "14px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.82rem",
                  fontWeight: "700",
                  color: isDark ? "rgba(255, 255, 255, 0.7)" : "#475569",
                  marginBottom: "5px",
                }}
              >
                📧 Email Address
              </label>
              <input
                type="email"
                placeholder="warrior@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  border: isDark ? "1px solid rgba(255, 255, 255, 0.14)" : "1px solid #cbd5e1",
                  background: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
                  color: isDark ? "#ffffff" : "#0f172a",
                  fontSize: "0.92rem",
                  fontWeight: "500",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* PASSWORD */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                <label
                  style={{
                    fontSize: "0.82rem",
                    fontWeight: "700",
                    color: isDark ? "rgba(255, 255, 255, 0.7)" : "#475569",
                  }}
                >
                  🔒 Password
                </label>
                <span
                  style={{
                    fontSize: "0.78rem",
                    color: "#ff6b35",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                  onClick={() => alert("Demo Password: warrior2026")}
                >
                  Forgot Password?
                </span>
              </div>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  border: isDark ? "1px solid rgba(255, 255, 255, 0.14)" : "1px solid #cbd5e1",
                  background: isDark ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
                  color: isDark ? "#ffffff" : "#0f172a",
                  fontSize: "0.92rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* REMEMBER WARRIOR CHECKBOX */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
              <input
                type="checkbox"
                id="remember"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ cursor: "pointer", accentColor: "#ff6b35" }}
              />
              <label
                htmlFor="remember"
                style={{
                  fontSize: "0.82rem",
                  color: isDark ? "rgba(255, 255, 255, 0.6)" : "#64748b",
                  cursor: "pointer",
                }}
              >
                Keep active warrior session open
              </label>
            </div>

            {/* SUBMIT BUTTON */}
            <button
              type="submit"
              disabled={loading || authSuccess}
              style={{
                width: "100%",
                padding: "14px",
                fontSize: "0.98rem",
                borderRadius: "12px",
                border: "none",
                color: "#ffffff",
                fontWeight: "700",
                cursor: loading || authSuccess ? "wait" : "pointer",
                background: authSuccess
                  ? "#10b981"
                  : "linear-gradient(135deg, #ff6b35 0%, #D85A30 100%)",
                boxShadow: "0 6px 20px rgba(255, 107, 53, 0.45)",
                transition: "all 0.25s ease",
              }}
            >
              {loading ? (
                <span>⚡ Powering Up Arena...</span>
              ) : authSuccess ? (
                <span>✓ Verified! Entering Arena...</span>
              ) : authMode === "signup" ? (
                <span>✨ Create Warrior Account →</span>
              ) : (
                <span>🚀 Enter Ignite Arena →</span>
              )}
            </button>
          </form>
        </div>
      </main>

      {/* 4. BOTTOM MINIMALIST FITNESS FOOTER */}
      <footer
        style={{
          position: "relative",
          zIndex: 10,
          padding: "14px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          background: isDark ? "rgba(11, 7, 24, 0.85)" : "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderTop: isDark ? "1px solid rgba(255, 107, 53, 0.15)" : "1px solid rgba(0, 0, 0, 0.08)",
          fontSize: "0.82rem",
          color: isDark ? "rgba(255, 255, 255, 0.6)" : "#64748b",
          transition: "all 0.3s ease",
        }}
      >
        <div>
          © {new Date().getFullYear()} Ignite Fitness • Autonomous AI-Powered Training & Nutrition OS.
        </div>
        <div style={{ display: "flex", gap: "18px", alignItems: "center" }}>
          <span style={{ color: "#ff6b35", fontWeight: "700" }}>🔥 14,820+ Sets Logged Today</span>
          <span>•</span>
          <span style={{ color: "#10b981", fontWeight: "700" }}>⚡ Streak Protection Active</span>
          <span>•</span>
          <span style={{ color: isDark ? "rgba(255, 255, 255, 0.7)" : "#475569" }}>Amandeep Verma</span>
        </div>
      </footer>
    </div>
  );
}

export default Login;
